// src/contexts/auth/hooks/form.ts
/** @jsxImportSource @emotion/react */

import React from "react";
import { useCallback } from "react";
import { makeAuthLogger } from "./storage";

export const useAuthForm = (deps: {
    email: string;
    passwordRef: React.MutableRefObject<string>;

    setEmailState: (v: string) => void;
    setPasswordState: (v: string) => void;
}) => {
    const { email, passwordRef, setEmailState, setPasswordState } = deps;

    const L = makeAuthLogger("form");

    // ✅ Keep stable identity; avoid depending on `email`.
    const setEmail = useCallback(
        (v: string) => {
            L.log("[setEmail] ->", { to: v });
            setEmailState(v);
        },
        [L, setEmailState],
    );

    const setPassword = useCallback(
        (v: string) => {
            L.log("[setPassword] ->", {
                hasPassword: !!v,
                len: v?.length ?? 0,
            });
            setPasswordState(v);
            passwordRef.current = v;
        },
        [L, passwordRef, setPasswordState],
    );

    const clearPassword = useCallback(() => {
        L.log("[clearPassword]");
        setPasswordState("");
        passwordRef.current = "";
    }, [L, passwordRef, setPasswordState]);

    const resetLoginForm = useCallback(() => {
        L.log("[resetLoginForm]");
        setEmailState("");
        setPasswordState("");
        passwordRef.current = "";
    }, [L, passwordRef, setEmailState, setPasswordState]);

    // `email` is unused by design (kept in deps for shape consistency).
    void email;

    return {
        setEmail,
        setPassword,
        clearPassword,
        resetLoginForm,
    };
};
